<div (click)="isUserPopupOpen = false" class="relative w-full">
  <!-- User Popup Card -->
  @if (isUserPopupOpen) {
    <div
      (click)="$event.stopPropagation()"
      class="absolute bottom-12 left-0 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-4 z-50 transform origin-bottom-left animate-in fade-in slide-in-from-bottom-2 duration-200">

      <div class="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-gray-800">
        <div class="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center font-bold text-white text-sm shrink-0">
          {{ getInitials(user$ | async) }}
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ (user$ | async)?.username || 'User' }}</div>
          <div class="text-xs text-gray-500 dark:text-gray-500 truncate">{{ (user$ | async)?.email }}</div>
        </div>
      </div>

      <!-- Theme Toggle -->
      <div class="flex flex-col gap-1 mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        <div class="text-[10px] font-bold text-gray-500 dark:text-gray-500 uppercase tracking-widest px-3 mb-2">Appearance</div>
        <div class="grid grid-cols-3 gap-1 bg-gray-100 dark:bg-black/20 p-1 rounded-lg">
          <button
            (click)="setTheme('light')"
            [class.bg-white]="themeService.themeMode() === 'light'"
            [class.dark:bg-gray-700]="themeService.themeMode() === 'light'"
            [class.shadow-sm]="themeService.themeMode() === 'light'"
            class="flex items-center justify-center p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-all"
            title="Light Mode">
            <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" [class.text-yellow-500]="themeService.themeMode() === 'light'" [class.dark:text-yellow-400]="themeService.themeMode() === 'light'" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />
            </svg>
          </button>
          <button
            (click)="setTheme('dark')"
            [class.bg-white]="themeService.themeMode() === 'dark'"
            [class.dark:bg-gray-700]="themeService.themeMode() === 'dark'"
            [class.shadow-sm]="themeService.themeMode() === 'dark'"
            class="flex items-center justify-center p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-all"
            title="Dark Mode">
            <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" [class.text-blue-500]="themeService.themeMode() === 'dark'" [class.dark:text-blue-400]="themeService.themeMode() === 'dark'" fill="currentColor" viewBox="0 0 20 20">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          </button>
          <button
            (click)="setTheme('system')"
            [class.bg-white]="themeService.themeMode() === 'system'"
            [class.dark:bg-gray-700]="themeService.themeMode() === 'system'"
            [class.shadow-sm]="themeService.themeMode() === 'system'"
            class="flex items-center justify-center p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-all"
            title="System Mode">
            <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" [class.text-purple-500]="themeService.themeMode() === 'system'" [class.dark:text-purple-400]="themeService.themeMode() === 'system'" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm2 1h10v7H5V6z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      <a
        routerLink="/knowledge-management"
        (click)="closePopup()"
        class="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors group mb-1">
        <svg class="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M4 7a2 2 0 012-2h3l2 2h7a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V7z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M8 12h8M8 16h5"></path>
        </svg>
        Knowledge Management
      </a>

      <button
        (click)="logout()"
        class="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-400/10 rounded-lg transition-colors group">
        <svg class="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
        </svg>
        Sign Out
      </button>
    </div>
  }

  <div class="flex items-center justify-between">
    <p class="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-widest">Veraxum</p>

    <button
      (click)="toggleUserPopup($event)"
      [class.text-blue-600]="isUserPopupOpen"
      [class.dark:text-blue-400]="isUserPopupOpen"
      class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-all focus:outline-none"
      title="Account settings">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
      </svg>
    </button>
  </div>
</div>
